// Brand configuration — the ONE place the product's name, its titles and its brand-adjacent
// external references come from (spec `platform-rebrand`, criteria A4/A5).
//
// Why this exists at all: `npm run build` runs `npm run update-locales` first, which refetches
// every `src/locales/*.json` from the upstream translator — so any brand word typed into a locale
// file is gone by the next prod build. Branding therefore lives here and is applied at render
// time (see `Language.ts`, task T2), never by editing locale bundles.
//
// Resolution order, lowest first:
//   1. the defaults below,
//   2. `REACT_APP_BRAND_*` from the build environment (`.env` on the box),
//   3. `BRAND_*` keys inside the runtime package config, when the app loads one
//      (`setBrandOverrides`, called next to `setAppConfig`).
// This app has no package config today — its resolution stops at the environment, and
// `setBrandOverrides` is kept so the three copies stay identical and it gains nothing to diverge.
//
// NOT branding — these are identifiers, and renaming them breaks production. They must stay as
// they are, and the brand check (`scripts/brand-check.mjs` in the KB repo) must not flag them:
//   · `/zaka/*` backend routes and the `/zaka-agents-container-frontend`, `/service-dashboard`
//     nginx paths;
//   · the `zakaio` deep-link scheme (`DEEP_LINK_PROTOCOL`) and the `io.zaka.app` store id;
//   · the `ProofSpace.WebLinker` global exported by the external SSO SDK;
//   · credential-definition ids, service and issuer DIDs;
//   · locale KEY names (`PROOFSPACE_ADMIN`, `DOCUMENT_TITLE`, …) — their values are branded at
//     render time, their keys are the contract with the translator.

export interface BrandData {
  /** Product name as it is shown to a person: "Civitas ID". */
  readonly NAME: string;
  /** Short form for tight spots (tab titles, mobile chrome). */
  readonly SHORT: string;
  /** Browser title of the container admin. */
  readonly ADMIN_TITLE: string;
  /** Browser title of the service dashboard. */
  readonly DASHBOARD_TITLE: string;
  /** Address a person is told to write to for support. */
  readonly SUPPORT_EMAIL: string;
  /** Base of the knowledge base / documentation. */
  readonly DOCS_URL: string;
  /** Terms of use shown at sign-up. */
  readonly TERMS_URL: string;
  /** Name of the end-user wallet app ("scan the QR with the … app"). */
  readonly WALLET_APP: string;
  /** Wallet app in the App Store. */
  readonly STORE_IOS: string;
  /** Wallet app in Google Play. */
  readonly STORE_ANDROID: string;
  /** Name of the SSI network the services live on, as written in help copy. */
  readonly NETWORK_NAME: string;
}

// The four values Viktor still has to settle (spec `platform-rebrand`, «Open values», task T4)
// keep their current ProofSpace values on purpose: SUPPORT_EMAIL, DOCS_URL, TERMS_URL and the
// wallet trio (WALLET_APP / STORE_IOS / STORE_ANDROID). They are wrong-but-visible here rather
// than scattered-and-invisible across components, which is the point of this module.
const DEFAULTS: BrandData = {
  NAME: 'Civitas ID',
  SHORT: 'Civitas',
  ADMIN_TITLE: 'Civitas ID Admin',
  DASHBOARD_TITLE: 'Civitas ID Dashboard',
  SUPPORT_EMAIL: 'team@proofspace.id',
  DOCS_URL: 'https://proofspace.atlassian.net/wiki/spaces/PSM',
  TERMS_URL: 'https://proofspace.page.link/terms-of-use',
  WALLET_APP: 'ProofSpace',
  STORE_IOS: 'https://apps.apple.com/app/proofspace/id1512258409',
  STORE_ANDROID: 'https://play.google.com/store/apps/details?id=io.zaka.app',
  NETWORK_NAME: 'ProofSpace',
};

const fromEnv: Partial<BrandData> = {
  NAME: process.env.REACT_APP_BRAND_NAME,
  SHORT: process.env.REACT_APP_BRAND_SHORT,
  ADMIN_TITLE: process.env.REACT_APP_BRAND_ADMIN_TITLE,
  DASHBOARD_TITLE: process.env.REACT_APP_BRAND_DASHBOARD_TITLE,
  SUPPORT_EMAIL: process.env.REACT_APP_BRAND_SUPPORT_EMAIL,
  DOCS_URL: process.env.REACT_APP_BRAND_DOCS_URL,
  TERMS_URL: process.env.REACT_APP_BRAND_TERMS_URL,
  WALLET_APP: process.env.REACT_APP_BRAND_WALLET_APP,
  STORE_IOS: process.env.REACT_APP_BRAND_STORE_IOS,
  STORE_ANDROID: process.env.REACT_APP_BRAND_STORE_ANDROID,
  NETWORK_NAME: process.env.REACT_APP_BRAND_NETWORK_NAME,
};

const defined = <T extends object>(o: T): Partial<T> =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== '')) as Partial<T>;

let _overrides: Partial<BrandData> = {};
let _brand: BrandData = {...DEFAULTS, ...defined(fromEnv)};

const rebuild = () => {
  _brand = {...DEFAULTS, ...defined(fromEnv), ...defined(_overrides)};
};

/**
 * Take the `BRAND_*` keys out of a runtime package config and apply them, so the brand can be
 * moved on the box without rebuilding the bundle. Call it wherever `setAppConfig` is called;
 * a config that carries no `BRAND_*` key changes nothing.
 */
export const setBrandOverrides = (cfg: any): void => {
  if (!cfg || typeof cfg !== 'object') return;
  const picked: Record<string, unknown> = {};
  for (const key of Object.keys(DEFAULTS)) {
    const value = cfg[`BRAND_${key}`] ?? (cfg.BRAND && cfg.BRAND[key]);
    if (typeof value === 'string' && value !== '') picked[key] = value;
  }
  _overrides = picked as Partial<BrandData>;
  rebuild();
};

/** The resolved brand. Read it at render time — never cache the strings at module load. */
export const brand = (): BrandData => _brand;

// ---------------------------------------------------------------------------------------------
// Rendering the brand over text that was written for the upstream product (A1, A3, A5).
//
// Locale bundles are refetched from the upstream translator on every build, so they keep saying
// "ProofSpace". Rather than editing them (which the next build undoes), every string that reaches
// a person passes through `applyBrand` — see `Language.ts`.
//
// Spans that are identifiers, not prose, are protected before anything is replaced: URLs, e-mail
// addresses, the `ProofSpace.WebLinker` SDK global, the `zakaio` scheme, the `io.zaka.app` store
// id, `/zaka/…` paths, and ALL_CAPS_SNAKE tokens (locale keys quoted inside help copy).
const PROTECTED = new RegExp(
  [
    'https?://[^\\s"\'<>)]+',              // links, incl. the docs wiki and the stores
    '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+',    // e-mail addresses
    'ProofSpace\\.WebLinker',              // the external SSO SDK global
    'io\\.zaka\\.app',                     // Google Play id of the wallet
    'zakaio',                              // deep-link scheme
    '/zaka[A-Za-z0-9/_-]*',                // backend routes and nginx paths
    '[A-Z][A-Z0-9_]{2,}',                  // locale keys and other SCREAMING_SNAKE identifiers
  ].join('|'),
  'g'
);

// One pass, never a chain: a chain re-reads its own output, so "ProofSpace app" would be renamed
// to the product and then renamed again by the bare-name rule. What follows the name decides which
// value applies — the wallet and the network are their own settings (spec «Open values», T4).
const phrase = (text: string): string => {
  const b = brand();
  return text.replace(
    /\b(?:Proof\s?Space|Zaka)\b(\s+(?:mobile app|app|wallet|network))?/gi,
    (_match: string, tail?: string) => {
      if (!tail) return b.NAME;
      return (/network/i.test(tail) ? b.NETWORK_NAME : b.WALLET_APP) + tail;
    }
  );
};

/**
 * Replace upstream product names in a piece of prose with the configured brand, leaving every
 * identifier span untouched. Safe to call on any string; it is a no-op for text that names
 * neither product.
 */
// The upstream literals a locale string can carry, and the setting that replaces each. While a
// setting still holds its upstream default the swap is a no-op — which is exactly the state of the
// four values Viktor has yet to settle (spec «Open values», T4).
const LINKS = (): Array<[string, string]> => {
  const b = brand();
  return [
    ['https://proofspace.atlassian.net/wiki/spaces/PSM', b.DOCS_URL],
    ['https://proofspace.page.link/terms-of-use', b.TERMS_URL],
    ['https://apps.apple.com/app/proofspace/id1512258409', b.STORE_IOS],
    ['https://play.google.com/store/apps/details?id=io.zaka.app', b.STORE_ANDROID],
    ['team@proofspace.id', b.SUPPORT_EMAIL],
  ];
};

const relink = (text: string): string => {
  let out = text;
  for (const [upstream, configured] of LINKS()) {
    if (upstream !== configured) out = out.split(upstream).join(configured);
  }
  return out;
};

export const applyBrand = (text: string): string => {
  if (!text || (!/proof\s?space/i.test(text) && !/zaka/i.test(text))) return text;
  text = relink(text);
  let out = '';
  let last = 0;
  PROTECTED.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PROTECTED.exec(text)) !== null) {
    out += phrase(text.slice(last, m.index)) + m[0];
    last = m.index + m[0].length;
  }
  return out + phrase(text.slice(last));
};
